<template>
    <!-- Forms Container -->
    <div class="forms-container" v-if="!isLoading && safeForms.length && safeFormConfig.length">
        <div v-if="!hasAnySectionAccess" class="border-0  rounded-lg p-4 text-center mb-3">
            <div class="d-flex flex-column align-items-center">
                <div class="rounded-circle bg-light d-flex align-items-center justify-content-center mb-2">
                    <i class="bi bi-lock fs-4 text-muted"></i>
                </div>
                <div class="fw-semibold text-dark text-xs">Access Restricted</div>
                <div class="text-muted text-xs mt-1">
                    Oops, you don't have access to this section. Please contact your administrator to assign access.
                </div>
            </div>
        </div>
        <div v-for="(form, fIdx) in safeFormConfig" :key="'form-' + form._mainId" class="form-wrapper w-100"
            :class="{ prev: currentFormIndex > fIdx, active: currentFormIndex === fIdx, next: currentFormIndex < fIdx }">
                <template v-if="form.sections && form.sections.length">
                    <template v-for="(section, sIdx) in form.sections"
                        :key="'section-' + form._mainId + '-' + section.id">
                        <!-- Only show section if it has access and action_name is not NO_ACCESS -->
                        <div v-if="hasSectionAccess(section)" class="mb-1">
                            <div class="bg-light border rounded-lg mb-2 p-3 section-card">
                                <!-- Section header -->
                                <div class="d-flex justify-content-between align-items-center pb-2 mb-2 border-bottom"
                                    v-if="section.title" @click="toggle_section(fIdx, sIdx)">

                                    <!-- Left side -->
                                    <div class="d-flex align-items-center gap-2 section-title-row">
                                        <span class="text-capitalize fw-7 text-xs section-title-text">{{ section.title }}</span>
                                        <span
                                            v-if="isAdvertisedPostSection(section)"
                                            class="badge bg-light text-muted border text-xxs fw-normal"
                                        >
                                            Questions 1-3 read only
                                        </span>
                                    </div>

                                    <!-- Right side: caret and actions -->
                                    <div class="d-flex align-items-center gap-2">
                                        <div v-if="userRole === 'System Admin' && hasWriteAccess(section)"
                                            @click.stop>
                                            <span
                                                v-if="section.is_verified"
                                                class="badge bg-success-subtle text-success border border-success-subtle text-xxs fw-semibold"
                                            >
                                                <i class="bi bi-check-circle-fill me-1"></i>Verified
                                            </span>
                                            <span
                                                v-else
                                                class="d-none badge bg-danger-subtle text-danger border border-danger-subtle text-xxs fw-semibold"
                                            >
                                                <i class="d-none bi bi-x-circle-fill me-1"></i>
                                            </span>
                                        </div>
                                        <i
                                            :class="is_section_open(fIdx, sIdx) ? 'fa-solid fa-chevron-up' : 'fa-solid fa-chevron-down'"></i>
                                    </div>
                                </div>

                                <!-- Work Experience Cards -->
                                <div class="mt-3"
                                    v-if="section.is_work_experience && section.we_blocks && section.we_blocks.length && is_section_open(fIdx, sIdx)">
                                    <div
                                        v-for="(block, bidx) in section.we_blocks"
                                        :key="'we-overview-' + section.id + '-' + bidx"
                                        class="we-block-card mb-3 p-3 border border-secondary-subtle rounded-3 bg-white shadow-sm"
                                    >
                                        <div class="d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom">
                                            <p class="fw-bold text-xs mb-0 text-dark">
                                                <i class="bi bi-building me-1 text-primary"></i>
                                                Experience {{ bidx + 1 }}
                                            </p>
                                            <span v-if="block.currently_work_here" class="badge bg-success text-xs">Currently working here</span>
                                        </div>
                                        <div class="row g-3">
                                            <template v-for="(field, fidx) in block.fields" :key="'we-ov-f-' + bidx + '-' + fidx">
                                                <div :class="['From Month','From Year','To Month','To Year'].includes(field.label) ? 'col-6 col-md-3' : (field.label === 'Reason for Leaving' ? 'col-12' : 'col-md-6')">
                                                    <label class="form-label fw-medium text-xs text-muted text-uppercase mb-1 d-block">
                                                        {{ field.label }}
                                                    </label>

                                                    <!-- Month dropdown -->
                                                    <select v-if="field.type === 'we_month' && safeForms[fIdx]"
                                                        class="form-select form-select-sm text-xs rounded-lg"
                                                        v-model="safeForms[fIdx][field.model]"
                                                        :disabled="!hasWriteAccess(section)">
                                                        <option value="" disabled>Month</option>
                                                        <option v-for="(monthLabel, monthIndex) in ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']" :key="'mo-'+(monthIndex + 1)" :value="String(monthLabel)">{{ monthLabel }}</option>
                                                    </select>

                                                    <!-- Year dropdown -->
                                                    <select v-else-if="field.type === 'we_year' && safeForms[fIdx]"
                                                        class="form-select form-select-sm text-xs rounded-lg"
                                                        v-model="safeForms[fIdx][field.model]"
                                                        :disabled="!hasWriteAccess(section)">
                                                        <option value="" disabled>Year</option>
                                                        <option v-for="y in 57" :key="'yr-'+(2027-y)" :value="String(2027-y)">{{ 2027 - y }}</option>
                                                    </select>

                                                    <!-- Textarea (Reason for Leaving) -->
                                                    <textarea v-else-if="field.type === 'textarea' && safeForms[fIdx]"
                                                        class="form-control form-control-sm text-xs bg-light border py-1 rounded-lg"
                                                        v-model="safeForms[fIdx][field.model]"
                                                        placeholder="Reason for leaving"
                                                        rows="2" maxlength="200"
                                                        :disabled="!hasWriteAccess(section)"></textarea>

                                                    <!-- Text input (Employer, Post Held) -->
                                                    <input v-else-if="safeForms[fIdx]"
                                                        type="text"
                                                        class="form-control form-control-sm text-xs bg-light border py-1 rounded-lg"
                                                        v-model="safeForms[fIdx][field.model]"
                                                        :placeholder="field.label"
                                                        :disabled="!hasWriteAccess(section)"
                                                    />

                                                    <!-- Fallback when safeForms not ready -->
                                                    <input v-else
                                                        type="text"
                                                        class="form-control form-control-sm text-xs bg-light border py-1 rounded-lg"
                                                        :value="field.value || 'No Answer'"
                                                        disabled
                                                    />
                                                </div>
                                            </template>
                                        </div>
                                    </div>
                                    <div v-if="!section.we_blocks.length" class="text-muted text-xs text-center py-3">
                                        No work experience entries submitted.
                                    </div>
                                </div>

                                <!-- Fields -->
                                <div class="mt-1"
                                    v-if="!section.is_work_experience && section.fields && section.fields.length && is_section_open(fIdx, sIdx) && safeForms[fIdx]">
                                    <template v-for="(field, fieldIndex) in section.fields"
                                        :key="'field-' + form._mainId + '-' + section.id + '-' + field.id">

                                        <!-- Main Question Container -->
                                        <div class="d-flex align-items-start mt-1 field-row">
                                            <div class="d-flex flex-grow-1 field-row-grow">
                                                <span class="text-xs fw-7 me-1 flex-shrink-0">{{fieldIndex + 1 }}.</span>
                                                <label class="flex-grow-1 text-dark text-xs text-capitalize fw-7 field-label">{{
                                                    field.label }}</label>
                                                <div v-if="['single_select', 'multi_select', 'radio', 'checkboxes'].includes(field.type)"
                                                    class="mb-1">
                                                </div>
                                            </div>
                                        </div>

                                        <!-- Main Question Answer -->
                                        <div class="mb-3 ms-2 field-desc">
                                            <!-- text -->
                                            <input v-if="field.type === 'text'" type="text"
                                                class="form-control form-control-sm text-xs bg-light border py-1 rounded-lg"
                                                v-model="safeForms[fIdx][field.model]" :placeholder="'No Answer'"
                                                :disabled="!canEditSectionQuestion(section, fieldIndex)" />

                                            <!-- textarea -->
                                            <textarea v-if="field.type === 'textarea'"
                                                class="form-control form-control-sm text-xs bg-light border py-1 rounded-lg"
                                                v-model="safeForms[fIdx][field.model]" :placeholder="'No Answer'"
                                                :disabled="!canEditSectionQuestion(section, fieldIndex)"></textarea>

                                            <!-- number -->
                                            <input v-if="field.type === 'number'" type="text"
                                                @input="handleNumberInput($event, field, fIdx)"
                                                class="form-control form-control-sm text-xs bg-light border py-1 rounded-lg"
                                                v-model="safeForms[fIdx][field.model]" :placeholder="'No Answer'"
                                                :disabled="!canEditSectionQuestion(section, fieldIndex)" />

                                            <!-- date -->
                                            <input v-if="field.type === 'date'" type="date"
                                                class="form-control form-control-sm text-xs bg-light border py-1 rounded-lg"
                                                v-model="safeForms[fIdx][field.model]"
                                                :disabled="!canEditSectionQuestion(section, fieldIndex)" />

                                            <!-- single select -->
                                            <div v-if="field.type === 'single_select' || field.type === 'radio'"
                                                class="radio-group">
                                                <div v-for="(option, optIndex) in field.options || []"
                                                    :key="`radio-${field.model}-${optIndex}`" class="form-check">
                                                    <input class="form-check-input" type="radio"
                                                        :name="`r-${field.model}`" :id="`${field.id}-${optIndex}`"
                                                        v-model="safeForms[fIdx][field.model]"
                                                        :value="String(option.value)"
                                                        @change="update_dependent_questions_visibility(field, safeForms[fIdx][field.model])"
                                                        :disabled="!canEditSectionQuestion(section, fieldIndex)" />
                                                    <label class="form-check-label text-xs"
                                                        :for="`${field.id}-${optIndex}`">
                                                        {{ option.text }}
                                                    </label>
                                                </div>
                                                <div v-if="!field.options?.length" class="text-muted text-xs">No
                                                    options</div>
                                            </div>

                                            <!-- multi select -->
                                            <div v-if="field.type === 'multi_select' || field.type === 'checkboxes'">
                                                <div v-for="(option, optIndex) in field.options || []"
                                                    :key="`chk-${field.model}-${optIndex}`" class="form-check">
                                                    <input class="form-check-input" type="checkbox"
                                                        :id="`${field.id}-${optIndex}`" :value="String(option.value)"
                                                        v-model="safeForms[fIdx][field.model]"
                                                        :disabled="!canEditSectionQuestion(section, fieldIndex)" />
                                                    <label class="form-check-label text-xs"
                                                        :for="`${field.id}-${optIndex}`">
                                                        {{ option.text }}
                                                    </label>
                                                </div>
                                            </div>

                                            <!-- file -->
                                            <div v-else-if="field.type === 'file' || field.type === 'pdf_file'">
                                                <template v-if="file_exitsts(fIdx, field)">
                                                    <div class="d-flex align-items-center justify-content-between flex-wrap">
                                                        <!-- Input box showing file name -->
                                                        <input
                                                            type="text"
                                                            class="form-control form-control-sm text-xs bg-light border py-1 rounded-lg verification-card"
                                                            :value="file_name(fIdx, field)"
                                                            readonly
                                                        />
                                                        <!-- Icons aligned to the right -->
                                                        <div class="d-flex gap-3">
                                                            <a
                                                                href="#"
                                                                class="text-dark text-decoration-none"
                                                                @click.prevent="open_file_preview(fIdx, field)"
                                                                title="Preview">
                                                                <i class="bi bi-eye"></i>
                                                            </a>
                                                            <a
                                                                v-if="canEditSectionQuestion(section, fieldIndex)"
                                                                href="#"
                                                                class="text-danger text-decoration-none"
                                                                @click.prevent="remove_file(fIdx, field.model)"
                                                                title="Remove">
                                                                <i class="bi bi-x-lg"></i>
                                                            </a>
                                                        </div>
                                                    </div>
                                                </template>

                                                <template v-else>
                                                    <input v-if="canEditSectionQuestion(section, fieldIndex)" type="file"
                                                        class="form-control form-control-sm text-xs bg-light border py-1 rounded-lg"
                                                        :id="field.id || ''"
                                                        accept=".pdf"
                                                        @change="handle_file_upload($event, field, fIdx)"
                                                        :required="field.required" />
                                                    <small class="text-muted text-xxs">Only PDF files are accepted</small>
                                                    <div v-if="!canEditSectionQuestion(section, fieldIndex)"
                                                        class="form-control form-control-sm text-xs bg-light border py-1 rounded-lg">
                                                        No file uploaded
                                                    </div>
                                                </template>
                                            </div>

                                            <!-- facial verification -->
                                            <div v-else-if="field.type === 'facial_verification'">
                                                <template v-if="file_exitsts(fIdx, field)">
                                                    <div class="d-flex align-items-center justify-content-between flex-wrap">
                                                        <!-- Input box showing file name -->
                                                        <input
                                                            type="text"
                                                            class="form-control form-control-sm text-xs bg-light border py-1 rounded-lg verification-card"
                                                            :value="file_name(fIdx, field)"
                                                            readonly
                                                        />
                                                        <!-- Icons aligned to the right -->
                                                        <div class="d-flex gap-3">
                                                            <a
                                                                href="#"
                                                                class="text-dark text-decoration-none"
                                                                @click.prevent="open_file_preview(fIdx, field)"
                                                                title="Preview">
                                                                <i class="bi bi-eye"></i>
                                                            </a>
                                                            <a
                                                                v-if="canEditSectionQuestion(section, fieldIndex)"
                                                                href="#"
                                                                class="text-danger text-decoration-none"
                                                                @click.prevent="remove_file(fIdx, field.model)"
                                                                title="Remove">
                                                                <i class="bi bi-x-lg"></i>
                                                            </a>
                                                        </div>
                                                    </div>
                                                </template>

                                                <div v-else-if="canEditSectionQuestion(section, fieldIndex)"
                                                    class="form-control form-control-sm text-xs bg-light border py-1 rounded-lg">
                                                    Facial verification component would appear here
                                                </div>
                                                <div v-else
                                                    class="form-control form-control-sm text-xs bg-light border py-1 rounded-lg">
                                                    No file uploaded - Upload disabled for read-only
                                                    access
                                                </div>
                                            </div>

                                            <!-- fallback -->
                                            <input
                                                v-if="['text','textarea','number','date','single_select','multi_select','checkboxes','file','pdf_file','facial_verification'].indexOf(field.type) === -1"
                                                type="text"
                                                class="form-control form-control-sm text-xs bg-white border py-1 rounded-lg"
                                                :id="field.id || ''" v-model="safeForms[fIdx][field.model]"
                                                :placeholder="field.placeholder || 'No Answer'"
                                                :required="field.required" :disabled="!canEditSectionQuestion(section, fieldIndex)" />
                                        </div>

                                        <!-- Dependent Questions (Unlimited Depth) -->
                                            <div
                                        v-if="field.dependent_questions && field.dependent_questions.length"
                                        class="mt-2 ms-4 ps-3 border-start border-3 border-primary"
                                        >
                                        <template
                                            v-for="row in flatten_dependents(field.dependent_questions)"
                                            :key="row.key"
                                        >
                                            <div
                                            v-show="row.node.is_visible"
                                            class="mb-3"
                                            :class="dependent_indent_class(row.depth)"
                                            >
                                            <!-- Label -->
                                            <div class="d-flex align-items-start mb-1 option-row">
                                                <span class="text-xs fw-7 me-1 flex-shrink-0">
                                                {{ fieldIndex + 1 }}.{{ row.path.join('.') }}
                                                </span>
                                                <label class="text-dark fw-7 text-xs text-capitalize option-label">
                                                {{ row.node.label }}
                                                <span v-if="row.node.required" class="text-danger">*</span>
                                                </label>
                                            </div>

                                            <!-- TEXT -->
                                            <input
                                                v-if="row.node.type === 'text'"
                                                type="text"
                                                class="form-control form-control-sm text-xs bg-light border-1 py-1 rounded-lg"
                                                v-model="safeForms[fIdx][row.node.model]"
                                                :disabled="!canEditSectionQuestion(section, fieldIndex)"
                                                @input="update_dependent_questions_visibility(row.node, safeForms[fIdx][row.node.model])"
                                            />

                                            <!-- TEXTAREA -->
                                            <textarea
                                                v-else-if="row.node.type === 'textarea'"
                                                class="form-control form-control-sm text-xs bg-light border-1 py-1 rounded-lg"
                                                v-model="safeForms[fIdx][row.node.model]"
                                                rows="3"
                                                :disabled="!canEditSectionQuestion(section, fieldIndex)"
                                                @input="update_dependent_questions_visibility(row.node, safeForms[fIdx][row.node.model])"
                                            ></textarea>

                                            <!-- NUMBER -->
                                            <input
                                                v-else-if="row.node.type === 'number'"
                                                type="text"
                                                class="form-control form-control-sm text-xs bg-light border-1 py-1 rounded-lg"
                                                v-model="safeForms[fIdx][row.node.model]"
                                                :disabled="!canEditSectionQuestion(section, fieldIndex)"
                                                @input="update_dependent_questions_visibility(row.node, safeForms[fIdx][row.node.model])"
                                            />

                                            <!-- DATE -->
                                            <input
                                                v-else-if="row.node.type === 'date'"
                                                type="date"
                                                class="form-control form-control-sm text-xs bg-light border-1 py-1 rounded-lg"
                                                v-model="safeForms[fIdx][row.node.model]"
                                                :disabled="!canEditSectionQuestion(section, fieldIndex)"
                                                @change="update_dependent_questions_visibility(row.node, safeForms[fIdx][row.node.model])"
                                            />

                                            <!-- SINGLE SELECT / RADIO -->
                                            <div
                                                v-else-if="row.node.type === 'single_select' || row.node.type === 'radio'"
                                                class="radio-group mt-2"
                                            >
                                                <div
                                                v-for="(option, optIndex) in row.node.options || []"
                                                :key="`${row.node.model}-r-${optIndex}`"
                                                class="form-check"
                                                >
                                                <input
                                                    class="form-check-input"
                                                    type="radio"
                                                    :name="row.node.model"
                                                    :id="`${row.node.model}-r-${optIndex}`"
                                                    v-model="safeForms[fIdx][row.node.model]"
                                                    :value="String(option.value)"
                                                    :disabled="!canEditSectionQuestion(section, fieldIndex)"
                                                    @change="update_dependent_questions_visibility(row.node, safeForms[fIdx][row.node.model])"
                                                />
                                                <label class="form-check-label text-xs" :for="`${row.node.model}-r-${optIndex}`">
                                                    {{ option.text }}
                                                </label>
                                                </div>
                                            </div>

                                            <!-- MULTI SELECT / CHECKBOXES -->
                                            <div
                                                v-else-if="row.node.type === 'multi_select' || row.node.type === 'checkboxes'"
                                                class="mt-2"
                                            >
                                                <div
                                                v-for="(option, optIndex) in row.node.options || []"
                                                :key="`${row.node.model}-c-${optIndex}`"
                                                class="form-check"
                                                >
                                                <input
                                                    class="form-check-input"
                                                    type="checkbox"
                                                    :id="`${row.node.model}-c-${optIndex}`"
                                                    :value="String(option.value)"
                                                    v-model="safeForms[fIdx][row.node.model]"
                                                    :disabled="!canEditSectionQuestion(section, fieldIndex)"
                                                    @change="update_dependent_questions_visibility(row.node, safeForms[fIdx][row.node.model])"
                                                />
                                                <label class="form-check-label text-xs" :for="`${row.node.model}-c-${optIndex}`">
                                                    {{ option.text }}
                                                </label>
                                                </div>
                                            </div>

                                            <!-- FILE -->
                                            <div v-else-if="row.node.type === 'file' || row.node.type === 'pdf_file'">
                                                <template v-if="file_exitsts(fIdx, row.node)">
                                                    <div class="d-flex align-items-center justify-content-between flex-wrap">
                                                        <!-- Input box showing file name -->
                                                        <input
                                                            type="text"
                                                            class="form-control form-control-sm text-xs bg-light border py-1 rounded-lg verification-card"
                                                            :value="file_name(fIdx, row.node)"
                                                            readonly
                                                        />
                                                        <!-- Icons aligned to the right -->
                                                        <div class="d-flex gap-3">
                                                            <a
                                                                href="#"
                                                                class="text-dark text-decoration-none"
                                                                @click.prevent="open_file_preview(fIdx, row.node)"
                                                                title="Preview">
                                                                <i class="bi bi-eye"></i>
                                                            </a>
                                                            <a
                                                                v-if="canEditSectionQuestion(section, fieldIndex)"
                                                                href="#"
                                                                class="text-danger text-decoration-none"
                                                                @click.prevent="remove_file(fIdx, row.node.model)"
                                                                title="Remove">
                                                                <i class="bi bi-x-lg"></i>
                                                            </a>
                                                        </div>
                                                    </div>
                                                </template>

                                                <template v-else>
                                                <input
                                                    v-if="canEditSectionQuestion(section, fieldIndex)"
                                                    type="file"
                                                    class="form-control form-control-sm text-xs bg-light border py-1 rounded-lg"
                                                    :id="row.node.model"
                                                    accept=".pdf"
                                                    @change="handle_file_upload($event, row.node, fIdx)"
                                                    :required="row.node.required && row.node.is_visible"
                                                />
                                                <small class="text-muted text-xxs">Only PDF files are accepted</small>
                                                <div v-if="!canEditSectionQuestion(section, fieldIndex)" class="form-control form-control-sm text-xs bg-light border py-1 rounded-lg">
                                                    No file uploaded
                                                </div>
                                                </template>
                                            </div>

                                            <!-- FALLBACK -->
                                            <input
                                                v-else
                                                type="text"
                                                class="form-control form-control-sm text-xs bg-light border-1 py-1 rounded-lg"
                                                v-model="safeForms[fIdx][row.node.model]"
                                                :disabled="!canEditSectionQuestion(section, fieldIndex)"
                                                @input="update_dependent_questions_visibility(row.node, safeForms[fIdx][row.node.model])"
                                            />
                                            </div>
                                        </template>
                                        </div>
                                    </template>
                                </div>

                                <!-- Verification / Actions - Only show for Write access and System Admin -->
                                <div v-if="!is_entity_user && hasWriteAccess(section)"
                                    class="d-flex justify-content-end align-items-center mt-3 p-2">
                                    <div class="d-flex gap-2 mt-2">
                                        <button v-if="!section.is_verified"
                                            class="d-none btn btn-sm text-xs shadow btn-primary-dark"
                                            :class="{ 'opacity-50': !is_verify_allowed }" :disabled="!is_verify_allowed"
                                            @click="is_verify_allowed ? section_verification(section.id) : null">
                                            Verify
                                        </button>
                                        <button class="d-none btn btn-sm text-xs shadow btn-primary-dark"
                                            :class="{ 'opacity-50': !is_request_information_allowed }"
                                            :disabled="!is_request_information_allowed" data-bs-toggle="modal"
                                            data-bs-target="#request_information_modal"
                                            @click="is_request_information_allowed ? prepare_request_information(fIdx, sIdx) : null">
                                            Request Information
                                        </button>
                                    </div>
                                </div>

                                <div
                                    v-if="!isReadOnly && hasAnyWriteAccess(form) && isLastVisibleSection(form, sIdx) && is_section_open(fIdx, sIdx)"
                                    class="d-flex justify-content-center align-items-center mt-3 pt-2 border-top save-actions-row"
                                >
                                    <button
                                        class="d-none btn btn-sm rounded-small text-xs shadow text-dark"
                                        :class="can_submit ? 'btn-primary-dark' : 'disabled'"
                                        :disabled="!can_submit"
                                        @click="can_submit ? (fIdx === safeFormConfig.length - 1 ? submitAnswers() : next_form()) : null"
                                    >
                                        Submit
                                    </button>
                                </div>
                            </div>
                        </div>
                    </template>
                </template>

                <div v-else class="text-muted small">No sections in this tab.</div>
        </div>
    </div>

    <div v-else-if="isLoading" class="text-center py-3">
        <div class="spinner-border" role="status"><span class="visually-hidden">Loading...</span></div>
    </div>
    <div v-else class="text-muted text-center">No form tabs found.</div>

    <!-- File preview modal shell -->
    <teleport to="body">
        <div v-if="showFilePreviewModal" @click.self="closeFilePreview">
            <div class="custom-modal-content">
                <div class="custom-modal-header">
                    <h5>File Preview</h5>
                    <button type="button" class="custom-modal-close" @click="closeFilePreview">&times;</button>
                </div>
            </div>
        </div>
    </teleport>

    <!-- Request info modal -->
    <teleport to="body">
        <div class="modal fade" id="request_information_modal" tabindex="-1"
            aria-labelledby="request_information_modal_label" aria-hidden="true">
            <div class="modal-dialog modal-dialog-centered">
                <div class="modal-content bg-white rounded-3 p-0 shadow-lg">
                    <div class="modal-header border-bottom-0 cute-header shadow-lg">
                        <p class="modal-title text-white text-xxs" id="request_information_modal_label">Request
                            Information</p>
                        <button type="button" class="modal-btn-close btn-close" data-bs-dismiss="modal"
                            aria-label="Close"></button>
                    </div>
                    <div class="modal-body">
                        <div v-if="answered_file_questions.length > 0" class="mb-3">
                            <div class="d-flex justify-content-between align-items-center mb-2">
                                <p class="text-xxs fw-bold mb-0">Files in current section:</p>
                            </div>
                            <div class="mt-3">
                                <div v-for="q in answered_file_questions" :key="q.section_question_id"
                                    class="file-list-item">
                                    <div class="d-flex justify-content-between align-items-center">
                                        <div class="d-flex align-items-center">
                                            <i class="bi bi-file-earmark me-2 text-primary"></i>
                                            <div>
                                                <div class="text-xxs">{{ q.question_text }}</div>
                                            </div>
                                        </div>
                                        <input type="checkbox" :value="q.section_question_id"
                                            :data-requested_field_text="q.question_text" class="form-check-input ms-3"
                                            @change="toggle_requested_field($event, q)" />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="modal-form-group form-group mb-2 d-flex flex-column">
                            <label for="request_info_note"
                                class="modal-form-label form-label text-start text-xxs">Notes</label>
                            <textarea v-model="request_info_note" id="request_info_note"
                                class="modal-form-control form-control text-xxs rounded-small p-2 shadow-sm"
                                rows="4"></textarea>
                        </div>
                    </div>
                    <div class="modal-footer border-0 p-2 d-flex justify-content-end">
                        <button type="button" class="btn btn-primary-dark btn-sm text-xxs shadow"
                            @click="request_information_submit">Request</button>
                    </div>
                </div>
            </div>
        </div>
    </teleport>
    <div v-if="preview_modal" @click.self="preview_modal = false">
        <FilePreviewComponent :presigned_url="selected_url" :file_name="selected_file_name" @close="close_the_file_preview" :key="selected_url" :file="{presigned_url: selected_url, url: selected_url,file_name: selected_file_name,name: selected_file_name}" />
    </div>
</template>
